import { compact, omit, uniq } from "lodash";
import { useCallback } from "react";

import { ERRORS, KEY } from "@/react/components/table/useTableMutations";
import { EMPTY_ARRAY } from "@/react/empty";

const useAgentAssignmentsQueryFactory = (
  agents,
  queues,
  getAgentById,
  getQueueById
) =>
  useCallback(
    (data) => {
      const assignments = (data ?? []).map((assignment) =>
        omit(assignment, [KEY, ERRORS])
      );

      const getQueuesForAgent = (agentId) =>
        uniq(
          compact(
            assignments
              .filter((assignment) => assignment.agentId === agentId)
              .map((assignment) => getQueueById(assignment.queueId)) ??
              EMPTY_ARRAY
          )
        );

      const getAgentsForQueue = (queueId) =>
        uniq(
          compact(
            assignments
              .filter((assignment) => assignment.queueId === queueId)
              .map((assignment) => getAgentById(assignment.agentId)) ??
              EMPTY_ARRAY
          )
        );

      const getQueuesAvailableForAgent = (agentId) => {
        const agentQueues = getQueuesForAgent(agentId);
        // All the queues where the agent is not assigned
        return (
          queues?.filter(
            (queue) =>
              !agentQueues.find((agentQueue) => queue.id === agentQueue.id)
          ) ?? EMPTY_ARRAY
        );
      };

      const getAgentsAvailableForQueue = (queueId) => {
        const queueAgents = getAgentsForQueue(queueId);
        // All the agents who are not assigned to this queue
        return (
          agents?.filter(
            (agent) =>
              !queueAgents.find(
                (queueAgent) => agent.sso.sso_id === queueAgent.sso.sso_id
              )
          ) ?? EMPTY_ARRAY
        );
      };

      return {
        getQueuesForAgent,
        getAgentsForQueue,
        getQueuesAvailableForAgent,
        getAgentsAvailableForQueue,
      };
    },
    [getQueueById, getAgentById, agents, queues]
  );

export default useAgentAssignmentsQueryFactory;
